import React, { FC, useState, ReactNode } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import styled from 'styled-components';

import AudioPlayer from 'src/presentation/components/forms/AudioPlayer';
import { Button } from 'src/presentation/components/agent_desktop_components';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import { getRecordingFileName } from 'src/presentation/components/pages/shared/utils/Utils';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import RecordingLibrary from 'src/presentation/components/pages/shared/RecordingLibrary';
import recordingsResource from 'src/actions/ivrs/recordingsResource';
import { StyledDeleteButton } from 'src/presentation/components/pages/shared/DeleteButton';
import useResource from 'src/presentation/hooks/useResource';
import XmlFileControl from './XmlFileControl';

type AudioSectionProps = {
  attr: string;
  description: string | ReactNode;
  itemId: string;
  formContext: any;
  label: string;
};

const AudioSection: FC<AudioSectionProps> = props => {
  const { description, formContext, label, attr } = props;
  const { updateState, workingState } = formContext;
  const url = get(workingState, attr, '');
  const [trackLabel, setTrackLabel] = useState(getRecordingFileName(url) as string);
  const [isRecordingLibraryVisible, setRecordingLibraryVisible] = useState(false);

  useResource(recordingsResource);

  const fileExt = url.split('.').pop();
  const isXmlFile = fileExt.toLowerCase() === 'xml';

  const sectionId = `audioSection-${attr}`;

  const onActionButtonPress = () => setRecordingLibraryVisible(true);

  const audioPlayerActions = (
    <AudioPlayerActions>
      <Button data-aid={`${sectionId}-transferButton`} type={Button.Types.SECONDARY} onClick={onActionButtonPress}>
        {url ? 'Update' : 'Select'} recording
      </Button>
    </AudioPlayerActions>
  );

  const onAudioSelected = newRecordingUrl => {
    updateState(set({}, attr, newRecordingUrl));
    setTrackLabel(getRecordingFileName(newRecordingUrl));
    setRecordingLibraryVisible(false);
  };

  const recordingLibrary = isRecordingLibraryVisible ? (
    <RecordingLibrary
      currentRecordingUrl={url}
      onCancel={() => setRecordingLibraryVisible(false)}
      onSave={onAudioSelected}
      selectionMode
    />
  ) : null;

  const xmlDescription = (
    <>
      {description} For assistance with XML files,{' '}
      <ExternalLink
        data-aid="support-link"
        label="contact Support"
        to="https://help.gladly.com/docs/contact-gladly-support"
      />
      {'.'}
    </>
  );

  if (isXmlFile) {
    return (
      <XmlFileControl
        audioLabel={trackLabel}
        audioPlayerActions={audioPlayerActions}
        data-aid={`${sectionId}-xmlFileControl`}
        description={xmlDescription}
        id={sectionId}
        label={label}
        recordingLibrary={recordingLibrary}
        url={url}
      />
    );
  }

  const audioPlayer = url ? (
    <AudioPlayer
      attr={attr}
      data-aid={`${sectionId}-player`}
      showBorder={true}
      value={url}
      hideAudioPlayerIfNoUrl
      trackLabel={trackLabel}
    />
  ) : null;

  return (
    <FieldContainer
      control={
        <AudioPlayerContainer id={sectionId} header={label} description={description}>
          {recordingLibrary}
          {audioPlayer}
          {audioPlayerActions}
        </AudioPlayerContainer>
      }
      data-aid={sectionId}
      description={description}
      label={label}
    />
  );
};

const AudioPlayerContainer = styled.div`
  position: relative;
  width: 100%;
  ${StyledDeleteButton} {
    visibility: visible;
  }
  h3 {
    color: ${p => p.theme.colors.black};
    font-size: ${p => p.theme.fontSize.base};
  }
  margin-bottom: ${p => p.theme.spacing.stackXLarge};
`;

const AudioPlayerActions = styled.div`
  align-items: center;
  display: flex;
  padding-top: ${p => p.theme.spacing.stackSmall};
`;

export default AudioSection;
